/**
 * The words the shop uses for a triage decision, and the constants that go with them.
 *
 * In `shared/` and not in `features/emails/` because two features need them today: the
 * inbox paints them on every row and `/estadisticas` uses them to label the breakdowns.
 * The rule of the repo is that a feature may import from `shared/` and never from another
 * feature, and this is exactly the case it exists for.
 *
 * No component, no hook, no API call: only the vocabulary. Anything that knows what an
 * email *is* stays in `features/emails/`.
 */
import type { Schema } from '@/shared/api/contract';

export type EmailCategory = Schema<'EmailCategory'>;
export type TriageDestination = Schema<'TriageDestination'>;
export type TriageSource = Schema<'TriageSource'>;
export type TriageAction = Schema<'TriageAction'>;

/**
 * The fifteen categories in the vocabulary the shop actually uses. Contract v10.24 added
 * «Confirmación de pedido a proveedor» and «Correo basura», and «Publicidad de proveedor»
 * became «Publicidad»: it now covers trade bulletins, fairs and webinars too.
 *
 * `Record<EmailCategory, string>` on purpose: when the back adds a category, this file
 * stops compiling instead of the screen quietly printing `pedido_urgente` at somebody.
 */
export const CATEGORY_LABELS: Record<EmailCategory, string> = {
  factura_proveedor: 'Factura de proveedor',
  aviso_bancario: 'Aviso bancario',
  solicitud_albaran: 'Solicitud de albarán',
  solicitud_factura: 'Solicitud de factura',
  pedido_con_albaran: 'Pedido con albarán',
  pedido_sin_albaran: 'Pedido sin albarán',
  pedido_ambiguo: 'Pedido ambiguo',
  solicitud_presupuesto: 'Solicitud de presupuesto',
  oferta_proveedor_respuesta: 'Oferta de proveedor',
  oferta_proveedor_publicidad: 'Publicidad',
  gestion_interna: 'Gestión interna',
  sospechoso: 'Sospechoso',
  otro: 'Otro',
  confirmacion_pedido_proveedor: 'Confirmación de pedido a proveedor',
  correo_basura: 'Correo basura',
};

export const CATEGORIES: readonly EmailCategory[] = Object.keys(CATEGORY_LABELS) as EmailCategory[];

/**
 * Categories the system no longer proposes (`rules.RETIRED_CATEGORIES` of the back): since
 * 2026-09-28 a supplier answering an order of Cubas is `gestion_interna`. The label stays
 * for the old decisions that carry it; it is not offered to choose.
 */
export const RETIRED_CATEGORIES: ReadonlySet<EmailCategory> = new Set<EmailCategory>([
  'confirmacion_pedido_proveedor',
]);

/** What a person can choose: every category but the retired ones, plus `current` when it is
 * one of those (an old decision, an old link), so a select still shows what it holds. */
export function categoryChoices(current?: EmailCategory | null): EmailCategory[] {
  return CATEGORIES.filter((each) => !RETIRED_CATEGORIES.has(each) || each === current);
}

/**
 * Where the triage says the message should go. The six of `TriageDestination`: contract
 * v10.44 added «Comerciales» (ADR 0038 of the back), whose addresses are the members of
 * the contact group of that name and which only ever receives advertising.
 */
export const DESTINATION_LABELS: Record<TriageDestination, string> = {
  administracion: 'Administración',
  maria: 'María',
  paulo: 'Paulo',
  paulo_y_administracion: 'Paulo y administración',
  se_queda: 'Se queda',
  comerciales: 'Comerciales',
};

/**
 * The people a destination can name. «Paulo y administración» is two of them; «Comerciales»
 * is the shop's sales people, the group of that name in Contactos.
 */
export type DestinationPerson = 'paulo' | 'maria' | 'administracion' | 'comerciales';

/**
 * Who each destination reaches, which is what gives it a colour on screen.
 *
 * `se_queda` reaches nobody and so has no colour: it is most of the inbox, and leaving it
 * plain is what makes the rows that do go to someone stand out.
 */
export const DESTINATION_PEOPLE: Record<TriageDestination, readonly DestinationPerson[]> = {
  administracion: ['administracion'],
  maria: ['maria'],
  paulo: ['paulo'],
  paulo_y_administracion: ['paulo', 'administracion'],
  se_queda: [],
  comerciales: ['comerciales'],
};

export const DESTINATIONS: readonly TriageDestination[] = Object.keys(
  DESTINATION_LABELS,
) as TriageDestination[];

/**
 * The one kind of mail «Comerciales» receives (contract v10.44, ADR 0038 of the back):
 * supplier advertising. Up to fifty people get it, so the back refuses anything else (a
 * correction gives 422, a learned criterion loses the destination) and the screens do not
 * offer it.
 */
export const COMERCIALES_CATEGORY: EmailCategory = 'oferta_proveedor_publicidad';

/**
 * The destinations a person may pick for an email that ends up as `category`: all of them,
 * except «Comerciales» for anything that is not advertising. `null` is «no lo sé» (a
 * learned criterion that does not change the kind), and then `appliesTo` decides, as the
 * back's `only_advertising` does: only a criterion limited to advertising may send there.
 */
export function destinationsFor(
  category: EmailCategory | null,
  appliesTo: readonly EmailCategory[] = [],
): TriageDestination[] {
  const advertising =
    category !== null
      ? category === COMERCIALES_CATEGORY
      : appliesTo.length > 0 && appliesTo.every((each) => each === COMERCIALES_CATEGORY);
  return DESTINATIONS.filter((each) => each !== 'comerciales' || advertising);
}

/** Who decided. A rule is free and certain; the model is what the accuracy figure measures. */
export const SOURCE_LABELS: Record<TriageSource, string> = {
  rule: 'Regla',
  llm: 'Modelo',
  human: 'Persona',
};

/**
 * What the system really did with the message.
 *
 * Every decision reads `none` until something acts, and that is worth saying out loud
 * on screen: somebody looking at a proposed destination has to know that nothing was
 * forwarded on the strength of it. Since contract v10 `none` can also mean "waiting for
 * a person": the panel says which, from `triage_plan`.
 */
export const ACTION_LABELS: Record<TriageAction, string> = {
  none: 'Nada',
  forwarded: 'Reenviado',
  labelled: 'Etiquetado',
  forwarded_and_labelled: 'Reenviado y etiquetado',
};

/**
 * Below this the destination is the mailbox's doubt rule, not the category's own.
 *
 * Mirrors `cubas.triage.routing.CONFIDENCE_FLOOR`. The back publishes the live value in
 * `GET /api/stats/triage` (`confidence_floor`), and the statistics screen uses that one;
 * this constant is the fallback for the inbox, which does not call that endpoint just to
 * decide whether to draw a warning.
 */
export const CONFIDENCE_FLOOR = 0.7;

export type TriageMode = Schema<'TriageMode'>;

/**
 * How much the system may do on its own for one mailbox and category (contract v10).
 *
 * Phase 1 starts with redirects in `confirm` and labels in `auto`: the system proposes,
 * a copy goes to the folder at classification, and nothing leaves the mailbox until a
 * person presses Correcto.
 */
/**
 * `auto` was «Solo» until 2026-10-01 and read as «only», the opposite of what it does,
 * right under «Solo propone». Wherever the modes are explained the words are these
 * three, and «solo» is never used for «by itself».
 */
export const MODE_LABELS: Record<TriageMode, string> = {
  shadow: 'Solo propone',
  confirm: 'Al confirmar',
  auto: 'Automático',
};

export const MODE_DESCRIPTIONS: Record<TriageMode, string> = {
  shadow: 'El sistema propone y no toca ningún correo.',
  confirm: 'El sistema propone y actúa cuando alguien pulsa Correcto o Corregir a…',
  auto: 'El sistema actúa en cuanto clasifica el correo, sin que nadie lo revise antes.',
};

export const MODES: readonly TriageMode[] = Object.keys(MODE_LABELS) as TriageMode[];
